import {
  CarrierLogo,
  Column,
  Rating,
  Row,
  ShowWhen,
  TextBadge,
  Typography,
} from '@/components'
import { HighlightsContainer } from '@/components/molecules/ModalBase/styles'
import { ThemeColor } from '@/enums'
import { useSegment } from '@/modules'
import { getFinalPrices } from '@/modules/price/price'
import { useAttributes } from '@/services/attributes'
import { theme } from '@/theme'
import {
  formatMoney,
  snakeToCamelCase,
  useIsMobile,
  useLanguage,
} from '@/utils'
import { AdaptiveTooltip, Button, Carousel, Skeleton } from '@surein/ui'
import { CheckIcon, ChevronDown, Sparkles, X, Ban } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CountdownComponent } from '../Countdown'
import { carrierRatingAmounts, useMessages } from '../../helpers'
import {
  BestOfferBadge,
  DesktopProductCard,
  DiscountPlaceholder,
  Highlight,
  InsurancePeriodSwitchWrapper,
  MobileCardFooter,
  MobileProductCard,
  Product,
  ProductCardsContainer,
  ProductFooter,
  ProductSection,
  ProductWrapper,
  ProductsContainer,
  RatingTooltipFootnote,
  RecommendationPriceSection,
  RecommendationPriceSection2,
  StarRating,
} from '../../styles'
import { RecommendationPaymentPeriodSwitch } from '../RecommendationPaymentPeriodSwitch/RecommendationPaymentPeriodSwitch'
import { CoverageNotSupported } from '../CoverageNotSupported/CoverageNotSupported'
import { LoadingIcon } from '@/icons'
import { forwardRef } from 'react'
import { GetRecommendationByIdApiResponse } from '@/api'
import { getMismatchedOptions } from './helpers'
import { SupportedCustomizationOptionsKeys } from '@/types/recommendation'

const formatMoneyConfig = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}

export interface RecommendationProduct {
  amountInsured?: number
  amountInsuredUnlimited?: boolean
  carrierData: {
    logoUrl: string | null
    claimHandlingRating?: number
    customerSatisfactionRating?: number
    responseTimeRating?: number
  }
  carrierId: string
  categoryId: string
  deductible?: number
  discountValue: number
  grossPrice: number
  offerTag?: string | null
  originalGrossPrice?: number
  overallRating?: number
  paymentPeriod?: string
  recommendationProductId: string
  // TODO: attributes
  parametersValues?: Record<string, any> // dynamicObjectPropType
  highlightedAttributes?: Record<string, boolean> // dynamicObjectPropType
  yesNoValues?: Record<string, 1 | 0> // dynamicObjectPropType
  contractCoverageRating?: number
  isSearchEnginePrice?: boolean | null
  status: 'success' | 'error' | null
}

interface ProductsProps {
  recommendationData: GetRecommendationByIdApiResponse
  onChoose: (id: string) => void
  openDetails?: () => void
  countdownTargetDateInMs?: number
  shouldShowRecommendationCustomization: boolean
  isRecommendationReloading?: boolean
}

interface ProductBadgeProps {
  isBestChoice?: boolean
  offerTag: string
  overwriteBestChoiceText?: string
}

export const ProductBadge = ({
  isBestChoice,
  offerTag,
  overwriteBestChoiceText,
}: ProductBadgeProps) => {
  const { t } = useTranslation()
  return (
    <BestOfferBadge isBestChoice={isBestChoice}>
      {isBestChoice ? <Sparkles size={16} /> : null}
      <Typography variant="footnote">
        {isBestChoice && overwriteBestChoiceText
          ? overwriteBestChoiceText
          : t(offerTag)}
      </Typography>
    </BestOfferBadge>
  )
}

export const Products = forwardRef<HTMLDivElement, ProductsProps>(
  (
    {
      recommendationData,
      onChoose,
      openDetails,
      countdownTargetDateInMs,
      shouldShowRecommendationCustomization,
      isRecommendationReloading,
    }: ProductsProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const { isMobile } = useIsMobile()
    const { t } = useTranslation()

    return (
      <ProductSection ref={ref}>
        <ProductCardsContainer data-testid="product-cards-container">
          {shouldShowRecommendationCustomization &&
          recommendationData?.categoryId !== 'CY' ? (
            <InsurancePeriodSwitchWrapper>
              <RecommendationPaymentPeriodSwitch />
            </InsurancePeriodSwitchWrapper>
          ) : null}

          <ProductsContainer
            columns={
              isMobile ? 1 : recommendationData?.recommendationProducts.length
            }
          >
            {isMobile ? (
              <Carousel startIndex={1}>
                {recommendationData?.recommendationProducts.map(
                  (productData, index) => {
                    const mismatchedOptions = getMismatchedOptions(
                      recommendationData,
                      productData.recommendationProductId,
                    )

                    const shouldShowMismatchWarning =
                      mismatchedOptions.length > 0

                    return (
                      <MobileProductCard
                        key={productData.recommendationProductId}
                        variant={index === 1 ? 'elevated' : 'default'}
                        shouldShowMismatchWarning={shouldShowMismatchWarning}
                      >
                        <ProductWrapper>
                          <ProductItem
                            productData={productData}
                            isBestChoice={
                              recommendationData.bestChoice ===
                              productData.recommendationProductId
                            }
                            onChoose={onChoose}
                            openDetails={openDetails}
                            countdownTargetDateInMs={countdownTargetDateInMs}
                            isRecommendationReloading={
                              isRecommendationReloading
                            }
                            mismatchedOptions={mismatchedOptions}
                          />
                        </ProductWrapper>
                      </MobileProductCard>
                    )
                  },
                )}
              </Carousel>
            ) : (
              recommendationData?.recommendationProducts.map(
                (productData, index) => {
                  const mismatchedOptions = getMismatchedOptions(
                    recommendationData,
                    productData.recommendationProductId,
                  )

                  return (
                    <DesktopProductCard
                      key={productData.recommendationProductId}
                      variant={index === 1 ? 'elevated' : 'default'}
                    >
                      <ProductWrapper>
                        <ProductItem
                          productData={productData}
                          isBestChoice={
                            recommendationData.bestChoice ===
                            productData.recommendationProductId
                          }
                          onChoose={onChoose}
                          openDetails={openDetails}
                          countdownTargetDateInMs={countdownTargetDateInMs}
                          isRecommendationReloading={isRecommendationReloading}
                          mismatchedOptions={mismatchedOptions}
                        />
                      </ProductWrapper>
                    </DesktopProductCard>
                  )
                },
              )
            )}
          </ProductsContainer>
        </ProductCardsContainer>
        <Typography bold color={ThemeColor.danger1} variant="badgeText">
          {t('offerValidFor')}
        </Typography>
      </ProductSection>
    )
  },
)
Products.displayName = 'Products'

const HighlightItem = ({
  isChecked,
  attributeName,
  parameterText = '',
}: {
  isChecked: boolean
  attributeName: string
  parameterText?: string
}) => {
  const icon = isChecked ? (
    <CheckIcon color={theme.color[ThemeColor.success1]} size="16px" />
  ) : (
    <X color={theme.color[ThemeColor.danger1]} size="16px" />
  )
  return (
    <Highlight>
      {icon}
      <Typography color={ThemeColor.b100} variant="footnote">
        {attributeName}
        {parameterText && `: ${parameterText}`}
      </Typography>
    </Highlight>
  )
}

const RatingTooltipContent = ({
  ratings,
}: {
  ratings: {
    contractCoverageRating?: number
    claimHandlingRating?: number
    customerSatisfactionRating?: number
    responseTimeRating?: number
  }
}) => {
  const { t } = useTranslation()
  return (
    <Column alignItems="start" gap="24px" width="400px">
      <Column alignItems="start" gap="8px">
        {Object.entries(ratings).map(
          ([key, value]) =>
            value && (
              <>
                <Typography color={ThemeColor.b100} variant="p2Body">
                  {t(`ratingTitles.${key}`)}
                </Typography>
                <Rating reverse value={value} />
              </>
            ),
        )}
      </Column>
      <RatingTooltipFootnote color={ThemeColor.b100} variant="p2Body">
        * {t('basedOnExperienceWithCarrier')}
      </RatingTooltipFootnote>
    </Column>
  )
}

interface ProductItemProps {
  productData: RecommendationProduct
  isBestChoice?: boolean
  onChoose: (recommendationProductId: string) => void
  openDetails?: () => void
  countdownTargetDateInMs?: number
  isRecommendationReloading?: boolean
  mismatchedOptions: {
    name: SupportedCustomizationOptionsKeys
    value: boolean
  }[]
}

const ProductItem = ({
  productData,
  onChoose,
  isBestChoice = false,
  openDetails,
  countdownTargetDateInMs,
  isRecommendationReloading,
  mismatchedOptions,
}: ProductItemProps) => {
  const { isMobile } = useIsMobile()
  const language = useLanguage()
  const { t } = useTranslation()
  const analytics = useSegment()
  const messages = useMessages(productData.categoryId)
  const {
    isLoading: isAttributesLoading,
    data: attributesData,
  } = useAttributes()
  const isProductAvailable =
    !(productData.status === 'error') ||
    productData.isSearchEnginePrice === false

  const yesNo = productData?.yesNoValues || {}
  const highlights = Object.fromEntries(
    Object.entries(productData.highlightedAttributes || {})
      .filter(([key, value]) => value && key in yesNo)
      .map(([key]) => [key, Boolean(yesNo[key])] as [string, boolean])
      .sort(([_k1, a], [_k2, b]) => Number(b) - Number(a)),
  )

  const ratings = {
    contractCoverageRating: productData.contractCoverageRating,
    claimHandlingRating: productData.carrierData.claimHandlingRating,
    customerSatisfactionRating:
      productData.carrierData.customerSatisfactionRating,
    responseTimeRating: productData.carrierData.responseTimeRating,
  }
  const areRatingsEmpty = Object.values(ratings).every((value) => !value)

  if (isAttributesLoading || !attributesData) {
    return null
  }

  const { finalPrice, discountedPrice } = getFinalPrices(
    productData.grossPrice,
    productData.discountValue,
    productData.paymentPeriod ?? 'yearly',
  )

  const parameters = productData?.parametersValues?.[language] || {}

  const handleDetailsButtonClick = async () => {
    await analytics.track('click_details_product')
    if (openDetails) {
      openDetails()
    }
  }

  return (
    <Product data-testid="product-item" isProductAvailable={isProductAvailable}>
      <Column gap="16px">
        <Row
          alignItems="center"
          gap="16px"
          justifyContent="space-between"
          wrap="wrap"
        >
          <CarrierLogo name={productData.carrierData.logoUrl} />
          {productData.offerTag && (
            <ProductBadge
              isBestChoice={isBestChoice}
              offerTag={productData.offerTag}
            />
          )}
        </Row>
        <Typography variant="p2Body">{messages.pricePaidToCarrier}</Typography>
        <Column gap="16px">
          <Column>
            <ShowWhen
              fallback={() => <DiscountPlaceholder />}
              when={Boolean(productData.discountValue)}
            >
              <RecommendationPriceSection>
                <Typography color={ThemeColor.b50} variant="p1Body">
                  {formatMoney(discountedPrice)}
                </Typography>
              </RecommendationPriceSection>
            </ShowWhen>
            <RecommendationPriceSection>
              <RecommendationPriceSection2>
                {isRecommendationReloading ? (
                  <Skeleton count={2} width="100%" height="14px" />
                ) : (
                  <>
                    <Typography bold color={ThemeColor.b100} variant="h4">
                      {isProductAvailable
                        ? formatMoney(finalPrice) || '----'
                        : '----,----'}
                    </Typography>
                    <Typography color={ThemeColor.b50} variant="badgeText">
                      / {t(`paymentPeriodOptions.${productData.paymentPeriod}`)}
                    </Typography>
                  </>
                )}
              </RecommendationPriceSection2>
            </RecommendationPriceSection>
          </Column>
          <ShowWhen
            fallback={() => <DiscountPlaceholder />}
            when={Boolean(productData.discountValue)}
          >
            <Row alignItems="center" gap="8px">
              <TextBadge
                backgroundColor={ThemeColor.success3}
                size="md"
                text={`-${productData.discountValue}% ${messages.sureinDiscount}`}
                textColor={ThemeColor.success1}
              />
              {countdownTargetDateInMs && (
                <CountdownComponent
                  targetDateInMs={countdownTargetDateInMs}
                  withTooltip
                />
              )}
            </Row>
          </ShowWhen>
        </Column>
        <ShowWhen when={!isMobile}>
          <Button
            onClick={() => onChoose(productData.recommendationProductId)}
            shape="square"
            variant={
              productData.offerTag === 'best_choice' && isProductAvailable
                ? 'primary'
                : 'muted'
            }
          >
            {isRecommendationReloading ? (
              <LoadingIcon />
            ) : isProductAvailable ? (
              messages.getCoverage
            ) : (
              <Ban />
            )}
          </Button>
        </ShowWhen>
        <ShowWhen when={!isMobile}>
          <CoverageNotSupported isProductAvailable={isProductAvailable} />
        </ShowWhen>

        <HighlightsContainer>
          <Typography color={ThemeColor.glass500} variant="footnote">
            {messages.mainAdvantages}:
          </Typography>
          <Column gap="8px">
            {productData.deductible ? (
              <HighlightItem
                attributeName={`${messages.deductible} ${formatMoney(
                  productData.deductible,
                  'euro',
                  formatMoneyConfig,
                )}`}
                isChecked={true}
              />
            ) : null}

            {Object.entries(highlights).map(([key, value]) => (
              <HighlightItem
                key={key}
                attributeName={attributesData.getAttributeLabel(key)}
                isChecked={value}
                parameterText={parameters[key]}
              />
            ))}
          </Column>
          {Object.entries(mismatchedOptions).length > 0 ? (
            <Column>
              <Typography color={ThemeColor.glass500} variant="footnote">
                {t('recommendations.customization.mismatchProductCardTitle')}
              </Typography>

              {Object.entries(mismatchedOptions).map(([key, value]) => (
                <HighlightItem
                  key={key}
                  attributeName={t(
                    `recommendations.customization.${snakeToCamelCase(
                      value.name,
                    )}.title`,
                  )}
                  isChecked={!!value.value}
                />
              ))}
            </Column>
          ) : null}
        </HighlightsContainer>
      </Column>
      <Column gap="32px">
        <Row gap="16px" justifyContent="space-between">
          <Column gap="4px">
            <Typography color={ThemeColor.glass500} variant="footnote">
              {messages.coverage}
            </Typography>
            <Typography variant="inter">
              {productData.amountInsuredUnlimited
                ? messages.unlimited
                : productData.amountInsured
                ? formatMoney(
                    productData.amountInsured,
                    'euro',
                    formatMoneyConfig,
                  )
                : null}
            </Typography>
          </Column>
          <Column gap="4px">
            <Typography color={ThemeColor.glass500} variant="footnote">
              {messages.deductible}
            </Typography>
            <Typography
              color={
                productData.deductible === 0
                  ? ThemeColor.success1
                  : ThemeColor.b100
              }
              variant="inter"
            >
              {productData.deductible
                ? formatMoney(productData.deductible, 'euro', formatMoneyConfig)
                : '--'}
            </Typography>
          </Column>
        </Row>
        <MobileCardFooter>
          <ShowWhen when={isMobile}>
            <Button
              onClick={() => onChoose(productData.recommendationProductId)}
              shape="square"
              size="sm"
              variant={
                productData.offerTag === 'best_choice' && isProductAvailable
                  ? 'primary'
                  : 'muted'
              }
            >
              {isProductAvailable ? messages.getCoverage : <Ban />}
            </Button>
          </ShowWhen>
          <ShowWhen when={isMobile}>
            <CoverageNotSupported isProductAvailable={isProductAvailable} />
          </ShowWhen>
          <ProductFooter>
            <a href="#details">
              <Button
                size="xs"
                variant="muted"
                onClick={handleDetailsButtonClick}
              >
                {messages.details}
              </Button>
            </a>
            <StarRating>
              {!areRatingsEmpty ? (
                <AdaptiveTooltip
                  // FIXME: AdaptiveTooltip type is marked as string only
                  // @ts-expect-error - we should fix AdaptiveTooltip types
                  content={<RatingTooltipContent ratings={ratings} />}
                >
                  <Row alignItems="center" gap="4px">
                    <Rating
                      iconSize={10}
                      ratingAmount={
                        carrierRatingAmounts[
                          productData.carrierId as keyof typeof carrierRatingAmounts
                        ]
                      }
                      reverse
                      value={productData.overallRating || 0}
                    />
                    <ChevronDown size={12} />
                  </Row>
                </AdaptiveTooltip>
              ) : (
                <Rating
                  iconSize={10}
                  ratingAmount={
                    carrierRatingAmounts[
                      productData.carrierId as keyof typeof carrierRatingAmounts
                    ]
                  }
                  reverse
                  value={productData.overallRating || 0}
                />
              )}
            </StarRating>
          </ProductFooter>
        </MobileCardFooter>
      </Column>
    </Product>
  )
}
